/**
 * Main-thread CLIENT for the off-main-thread NS solver worker — PHASE 2.
 *
 * Wraps a `Worker` running `solverWorker.ts` behind a Promise-based step API,
 * with the protocol's seq/epoch staleness guards, a per-step timeout, a single
 * retry, and a TRANSPARENT fallback to the main-thread `runNSStep` on ANY
 * failure (no Worker available, worker crash, timeout, parse error, stale-only
 * responses). The UI must never hang.
 *
 * ──────────────────────────────────────────────────────────────────────────
 * SAFETY MODEL (CLAUDE.md rule #1 — solver integrity is highest priority)
 * ──────────────────────────────────────────────────────────────────────────
 *   • This module is ONLY reached when the `useWorkerSolver` flag is ON. When
 *     the flag is OFF the live run loop calls `runNSStep` directly and this file
 *     is never imported, so the default path is provably untouched.
 *   • The worker's `workerStepReducer` is a bit-identical replica of
 *     `runNSStep`'s existing-state physics core (proven by workerStepParity.test).
 *     The fallback path here is literally `runNSStep` itself, so a fallback step
 *     is numerically identical to a non-worker step — degrading to the main
 *     thread changes latency, never results.
 *   • Worker unavailability (SSR / jsdom / test / no `Worker` global) is detected
 *     up front; `step()` then always resolves via the main-thread fallback.
 */

import type { Node, Edge } from 'reactflow';
import type { SimulationState, Site } from '../../types';
import type { NSSolverState } from '../types';
import { runNSStep, type NSStepResult } from '../integration/stepAdapter';
import { syncNSToNodes, syncNSToEdges } from '../integration/stateSync';
import {
  createSequencer,
  isResponseStale,
  type NSWorkerResponse,
  type ResultResponse,
  type StartRequest,
  type StepRequest,
  type Sequencer,
} from './protocol';
import {
  serializeSolverState,
  deserializeSolverState,
  type SerializedSolverState,
} from './serialization';

/** Default per-step timeout before we abandon the worker and fall back. */
export const DEFAULT_STEP_TIMEOUT_MS = 4000;

/**
 * R1-A3 M4 (W7 caveat 1) — one-time note surfaced when the water-hammer
 * acoustic overlay forces the main-thread solver path. Overlay states live in
 * a WeakMap keyed on solver-state OBJECT IDENTITY (stepAdapter), and the
 * worker round-trip deserializes a NEW state object — so on the worker path
 * armed overlays would silently vanish (workerStepReducer has no acoustic
 * calls). Rather than silently dropping physics, the client routes acoustic
 * runs through the identical main-thread `runNSStep` and says so once.
 */
export const ACOUSTIC_MAIN_THREAD_NOTE =
  'Water-hammer acoustics run on the main thread — the worker solver is bypassed while the acoustic overlay is enabled.';

export interface WorkerClientOptions {
  /** Per-step timeout (ms). After this we fall back to the main thread. */
  timeoutMs?: number;
}

/**
 * Result of a client step — same shape the run loop's main-thread path returns
 * (`NSStepResult`), plus telemetry flags so callers/tests can see which path ran.
 */
export interface WorkerStepOutcome extends NSStepResult {
  /** True when the result came from the worker; false when we fell back. */
  ranOnWorker: boolean;
  /** Why we fell back (undefined when `ranOnWorker`). */
  fallbackReason?: string;
}

/**
 * Feature-detect a usable `Worker`. False in SSR, jsdom, Node, and any test
 * env without a real Worker. We also require `import.meta.url`-style module
 * worker support implicitly by attempting construction lazily (guarded).
 */
export function isWorkerAvailable(): boolean {
  return typeof Worker !== 'undefined';
}

/**
 * The NS solver worker client. One instance per live run. Holds the Worker,
 * the request/epoch sequencers, and the retained authoritative state mirror so
 * that — even on the worker path — the main thread keeps a serialized snapshot
 * it can hand to `runNSStep` for a numerically identical fallback.
 */
export class NSWorkerClient {
  private worker: Worker | null = null;
  private readonly reqSeq: Sequencer = createSequencer();
  private epoch = 0;
  /** Highest request seq we have dispatched (for response-staleness checks). */
  private latestRequestSeq = 0;
  private readonly timeoutMs: number;
  /** Pending step resolvers, keyed by the requestSeq they await. */
  private readonly pending = new Map<
    number,
    { resolve: (r: ResultResponse) => void; reject: (e: Error) => void; timer: ReturnType<typeof setTimeout> }
  >();
  /** True once the worker has emitted a fatal error and is considered dead. */
  private workerDead = false;
  /** R1-A3 M4 — the acoustic main-thread note has been surfaced (once per client). */
  private acousticNoteSurfaced = false;

  constructor(opts: WorkerClientOptions = {}) {
    this.timeoutMs = opts.timeoutMs ?? DEFAULT_STEP_TIMEOUT_MS;
  }

  /** Lazily construct the worker. Returns null if construction is impossible. */
  private ensureWorker(): Worker | null {
    if (this.workerDead) return null;
    if (this.worker) return this.worker;
    if (!isWorkerAvailable()) return null;
    try {
      // Vite resolves this `new URL(..., import.meta.url)` form to a hashed
      // worker chunk at build time. `type: 'module'` matches the ESM source.
      this.worker = new Worker(new URL('./solverWorker.ts', import.meta.url), {
        type: 'module',
      });
      this.worker.onmessage = (ev: MessageEvent) => this.onMessage(ev.data as NSWorkerResponse);
      this.worker.onerror = () => this.killWorker('worker onerror');
      this.worker.onmessageerror = () => this.killWorker('worker onmessageerror');
      return this.worker;
    } catch {
      this.workerDead = true;
      return null;
    }
  }

  private onMessage(resp: NSWorkerResponse): void {
    if (resp.type === 'progress') return; // advisory only
    // Drop responses from a superseded epoch / older request.
    if (isResponseStale(resp, this.epoch, this.latestRequestSeq)) return;
    const waiter = this.pending.get(resp.requestSeq);
    if (!waiter) return; // already timed out / resolved
    clearTimeout(waiter.timer);
    this.pending.delete(resp.requestSeq);
    if (resp.type === 'error') {
      waiter.reject(new Error(resp.message));
      return;
    }
    waiter.resolve(resp);
  }

  /** Mark the worker dead and reject every in-flight step so they fall back. */
  private killWorker(reason: string): void {
    this.workerDead = true;
    try {
      this.worker?.terminate();
    } catch {
      /* ignore */
    }
    this.worker = null;
    for (const [, waiter] of this.pending) {
      clearTimeout(waiter.timer);
      waiter.reject(new Error(reason));
    }
    this.pending.clear();
  }

  /** Post a request and await its matching result, with a timeout. */
  private postAndAwait(req: StartRequest | StepRequest): Promise<ResultResponse> {
    const worker = this.ensureWorker();
    if (!worker) return Promise.reject(new Error('worker unavailable'));
    return new Promise<ResultResponse>((resolve, reject) => {
      const timer = setTimeout(() => {
        this.pending.delete(req.seq);
        reject(new Error(`worker step timeout after ${this.timeoutMs}ms`));
      }, this.timeoutMs);
      this.pending.set(req.seq, { resolve, reject, timer });
      try {
        worker.postMessage(req);
      } catch (e) {
        clearTimeout(timer);
        this.pending.delete(req.seq);
        reject(e instanceof Error ? e : new Error(String(e)));
      }
    });
  }

  /**
   * Open a new epoch and seed the worker with `state`. Call on (re)build /
   * topology change so stale in-flight steps from a prior network are dropped.
   * Bumps the epoch; the next `step` belongs to the new epoch.
   */
  startEpoch(): number {
    this.epoch += 1;
    return this.epoch;
  }

  /**
   * Advance ONE transient step. Tries the worker (with a single retry on
   * failure), then falls back to `runNSStep` on the main thread. ALWAYS resolves
   * — never rejects — so the caller's run loop cannot hang.
   *
   * @param nodes/edges/simState/site Canvas + UI context (for node sync + fallback).
   * @param existingState The authoritative live solver state (built by runNSStep
   *                       on a prior step). MUST be non-null for the worker path;
   *                       when null we always run `runNSStep` (which builds it).
   * @param dtInput The UI dt budget for this step.
   */
  async step(
    nodes: Node[],
    edges: Edge[],
    simState: SimulationState,
    site: Site | null,
    existingState: NSSolverState | null,
    dtInput: number,
  ): Promise<WorkerStepOutcome> {
    // R1-A3 M4 (W7 caveat 1) — acoustic overlays are main-thread-only: the
    // WeakMap in stepAdapter is keyed on solver-state identity, and a worker
    // round-trip deserializes a NEW object, so armed overlays would be
    // silently dropped mid-transient. Force the IDENTICAL main-thread path
    // (the fallback IS runNSStep — scheduling changes, numerics never do) and
    // surface a one-time note. Checked FIRST so the routing reason is stable
    // for the whole acoustic run.
    if (simState.enableAcoustic) {
      const outcome = this.fallback(
        nodes, edges, simState, site, existingState, dtInput,
        'acoustics enabled — overlays are main-thread-only',
      );
      if (!this.acousticNoteSurfaced) {
        this.acousticNoteSurfaced = true;
        outcome.simState = {
          ...outcome.simState,
          warnings: [...(outcome.simState.warnings ?? []), ACOUSTIC_MAIN_THREAD_NOTE],
        };
      }
      return outcome;
    }

    // Without a built state, or without a usable worker, run the main thread.
    // (runNSStep builds the network on a null existingState; the worker cannot.)
    if (!existingState || !isWorkerAvailable() || this.workerDead) {
      return this.fallback(
        nodes,
        edges,
        simState,
        site,
        existingState,
        dtInput,
        !existingState ? 'no built state (initial build)' : 'worker unavailable',
      );
    }

    const settings = {
      relaxationFactor: simState.relaxationFactor,
      convergenceTolerance: simState.convergenceTolerance,
      autoTimeStep: simState.autoTimeStep,
    };

    let serialized: SerializedSolverState;
    try {
      serialized = serializeSolverState(existingState);
    } catch (e) {
      return this.fallback(
        nodes,
        edges,
        simState,
        site,
        existingState,
        dtInput,
        `serialize failed: ${e instanceof Error ? e.message : String(e)}`,
      );
    }

    // Try the worker, then one retry, then fall back.
    for (let attempt = 0; attempt < 2; attempt++) {
      const seq = this.reqSeq.next();
      this.latestRequestSeq = seq;
      const req: StepRequest = {
        type: 'step',
        direction: 'request',
        seq,
        epoch: this.epoch,
        dt: dtInput,
        // Always send authoritative state so a worker restart / first step after
        // a retry resyncs deterministically (defeats stale-state drift).
        state: serialized,
        // Thread the user's solver knobs every step so the worker's
        // deriveSolverConfig + autoTimeStep match the main-thread runNSStep
        // exactly (no StartRequest is sent, so retainedSettings would be {}).
        settings,
      };
      try {
        const result = await this.postAndAwait(req);
        return this.applyWorkerResult(nodes, edges, simState, result, dtInput);
      } catch (e) {
        // First failure → retry once (the worker may have just been (re)created).
        // Second failure → fall through to the main-thread fallback below.
        if (attempt === 1 || this.workerDead) {
          return this.fallback(
            nodes,
            edges,
            simState,
            site,
            existingState,
            dtInput,
            `worker step failed: ${e instanceof Error ? e.message : String(e)}`,
          );
        }
      }
    }

    // Unreachable in practice, but keeps the function total.
    return this.fallback(nodes, edges, simState, site, existingState, dtInput, 'worker exhausted retries');
  }

  /** Turn a worker `ResultResponse` into the run loop's `NSStepResult` shape. */
  private applyWorkerResult(
    nodes: Node[],
    edges: Edge[],
    simState: SimulationState,
    result: ResultResponse,
    dtInput: number,
  ): WorkerStepOutcome {
    const state = deserializeSolverState(result.state);
    const updatedNodes = syncNSToNodes(nodes, state);
    // D-F5: identical edge sync to the main-thread runNSStep path.
    const updatedEdges = syncNSToEdges(edges, state, simState.isRunning === true);
    return {
      ranOnWorker: true,
      nodes: updatedNodes,
      edges: updatedEdges,
      simState: {
        // `runNSStep` advances `time` by the UI dt budget (dt_input). Reproduce
        // that EXACTLY using the same dtInput the caller passed to `step()`.
        time: simState.time + dtInput,
        timeStep: result.dt,
        warnings: result.warnings,
        errors: [],
        metrics: {
          ...simState.metrics,
          convergence: {
            residual: isFinite(result.residuals.mass)
              ? result.residuals.mass
              : Number.POSITIVE_INFINITY,
            iterations: state.iteration,
            isConverged: result.converged,
            energyClosurePct: result.energyClosurePct ?? undefined,
          },
        },
      },
      nsSolverState: state,
    };
  }

  /** Run the main-thread `runNSStep` (the identical legacy physics path). */
  private fallback(
    nodes: Node[],
    edges: Edge[],
    simState: SimulationState,
    site: Site | null,
    existingState: NSSolverState | null,
    dtInput: number,
    reason: string,
  ): WorkerStepOutcome {
    const r = runNSStep(nodes, edges, simState, site, existingState, dtInput);
    return { ...r, ranOnWorker: false, fallbackReason: reason };
  }

  /** Tear down the worker (call when the run stops / topology changes). */
  dispose(): void {
    try {
      this.worker?.terminate();
    } catch {
      /* ignore */
    }
    this.worker = null;
    for (const [, waiter] of this.pending) {
      clearTimeout(waiter.timer);
      waiter.reject(new Error('client disposed'));
    }
    this.pending.clear();
  }
}
